import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Flashcards API service
 * 
 * Provides access to flashcard decks and spaced repetition study features.
 */
export const FlashcardApi = {
  /**
   * Create flashcard deck (typically called after AI generation)
   * @param {Object} data - Flashcard deck data
   * @param {number} data.userId - User ID
   * @param {number} [data.endeavorId] - Endeavor ID (optional)
   * @param {string} data.title - Deck title
   * @param {string} [data.description] - Deck description
   * @param {number[]} [data.documentIds] - Source document IDs
   * @param {Object[]} data.cards - Array of flashcard cards
   * @param {string} data.cards[].frontText - Front of card
   * @param {string} data.cards[].backText - Back of card
   * @param {number[]} [data.cards[].sourceChunkIds] - Source chunk IDs
   * @param {number} [data.cards[].position] - Card position in deck
   * @param {number} [data.generationCostUsd] - Generation cost in USD
   * @returns {Promise<Object>} Created flashcard deck with cards
   */
  createFlashcardDeck: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get flashcard decks (simple list)
   * @param {Object} [options] - Filter options
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @returns {Promise<Object[]>} List of flashcard decks
   */
  getFlashcardDecks: async (options = {}) => {
    const { endeavorId } = options;
    const queryParams = new URLSearchParams();
    
    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    
    const queryString = queryParams.toString();
    const url = `${DATA_SERVICE_BASE_URL}/api/flashcards/decks${queryString ? '?' + queryString : ''}`;
    
    return fetchWithAuth(url, {
      method: 'GET',
    });
  },

  /**
   * List and filter flashcard decks with pagination
   * @param {Object} [options] - Query options
   * @param {number} [options.page=0] - Page number (0-indexed)
   * @param {number} [options.size=20] - Page size
   * @param {number} [options.endeavorId] - Filter by endeavor ID
   * @param {number} [options.documentId] - Filter by document ID
   * @param {string} [options.title] - Filter by title (partial match)
   * @param {string} [options.createdAfter] - ISO datetime string
   * @param {string} [options.createdBefore] - ISO datetime string
   * @param {string} [options.sortBy="createdAt"] - Sort field
   * @param {string} [options.sortDirection="DESC"] - Sort direction: "ASC" or "DESC"
   * @returns {Promise<Object>} Paginated flashcard deck results
   */
  listFlashcardDecks: async (options = {}) => {
    const {
      page = 0,
      size = 20,
      endeavorId,
      documentId,
      title,
      createdAfter,
      createdBefore,
      sortBy = 'createdAt',
      sortDirection = 'DESC',
    } = options;

    const queryParams = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortBy,
      sortDirection,
    });

    if (endeavorId) queryParams.set('endeavorId', endeavorId);
    if (documentId) queryParams.set('documentId', documentId);
    if (title) queryParams.set('title', title);
    if (createdAfter) queryParams.set('createdAfter', createdAfter);
    if (createdBefore) queryParams.set('createdBefore', createdBefore);

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/list?${queryParams.toString()}`, {
      method: 'GET',
    });
  },

  /**
   * Get single flashcard deck with all cards
   * @param {number} id - Flashcard deck ID
   * @returns {Promise<Object>} Flashcard deck with cards
   */
  getFlashcardDeck: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/${id}`, {
      method: 'GET',
    });
  },

  /**
   * Update flashcard deck (e.g., generation cost)
   * @param {number} id - Flashcard deck ID
   * @param {Object} data - Updated deck data
   * @param {number} [data.generationCostUsd] - Generation cost in USD
   * @returns {Promise<Object>} Updated flashcard deck
   */
  updateFlashcardDeck: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  /**
   * Get due flashcards for review (spaced repetition)
   * @param {number} deckId - Flashcard deck ID
   * @returns {Promise<Object[]>} List of due flashcard cards with review data
   */
  getDueCards: async (deckId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/${deckId}/due`, {
      method: 'GET',
    });
  },

  /**
   * Update flashcard review (SM-2 spaced repetition algorithm)
   * @param {number} cardId - Flashcard card ID
   * @param {Object} reviewData - Review data
   * @param {number} reviewData.quality - Quality rating (0-5)
   * @returns {Promise<Object>} Updated review data with next review date
   */
  updateCardReview: async (cardId, reviewData) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/cards/${cardId}/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reviewData),
    });
  },

  /**
   * Get review statistics for a deck
   * @param {number} deckId - Flashcard deck ID
   * @returns {Promise<Object>} Deck statistics (total cards, due cards, mastery stats)
   */
  getDeckStats: async (deckId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/${deckId}/stats`, {
      method: 'GET',
    });
  },

  /**
   * Delete flashcard deck
   * @param {number} id - Flashcard deck ID
   * @returns {Promise<void>} Delete confirmation
   */
  deleteFlashcardDeck: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/flashcards/decks/${id}`, {
      method: 'DELETE',
    });
  },
};
